import { useEffect, useState } from "react";
import { classNames } from "primereact/utils";

import { useDataTable } from "../../../shared/hooks/DataTable";
import { useToolbar } from "../../../shared/hooks/Toolbar";
import { InvoiceSummaryService } from "./InvoiceSummaryService";
import { DetailDialog } from "./components/detail-dialog";
import { CreateInvoiceSummaryDialog } from "./components/createInvoiceSummaryDialog";
import {
  ODataPeriod,
  OInvoiceSummaryField,
  OInvoiceSummaryStatus,
  InvoiceSummary,
} from "../../../shared/constants";
import { UpdateInvoiceSummaryDialog } from "./components/updateInvoiceSummaryDialog";
import {
  exportExcel,
  invoiceStatusClassMap,
  convertDataPeriod,
  downloadXml,
} from "./invoiceSummaryUtils";
import { ViewSummaryDialog } from "./components/viewSummaryDialog";
import { IamColumn } from "../../../shared/hooks/DataTableBasic";
import { SummaryLog } from "./components/summaryLogs";
import { CurrencyService } from "../../danh-muc/tien-te/CurrencyService";
import { useSign } from "../../../shared/component/Sign";
import { ToastService } from "../../../AppToast";
import { LoadingService } from "../../../AppLoading";

export function BangTongHop() {
  const [showDetail, setShowDetail] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<
    (InvoiceSummary & { actions: any }) | null
  >(null);
  const [showCreate, setShowCreate] = useState<boolean>(false);
  const [showUpdate, setShowUpdate] = useState<boolean>(false);
  const [showView, setShowView] = useState<boolean>(false);
  const [showSummaryLogs, setShowSummaryLogs] = useState<boolean>(false);
  const [currencies, setCurrencies] = useState<any>([]);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [highestSummaryDataNumber, setHighestSummaryDataNumber] =
    useState<number>(0);
  const [disableUpdate, setDisableUpdate] = useState<boolean>(false);

  const BangTongHopColumns: IamColumn[] = [
    {
      field: OInvoiceSummaryField.STATUS,
      header: "Trạng thái",
      width: 120,
      matchMode: "contains",
      dataType: "custom",
      filterType: "multiselect",
      filterOptions: [
        {
          label: "Đang gửi",
          value: OInvoiceSummaryStatus.SENT,
        },
        {
          label: "Chờ ký",
          value: OInvoiceSummaryStatus.WAIT_SIGN,
        },
        {
          label: "Thành công",
          value: OInvoiceSummaryStatus.SUCCESSFUL,
        },
        {
          label: "Có lỗi",
          value: OInvoiceSummaryStatus.ERROR,
        },
      ],
      customCell: (rowData) => {
        return (
          <span
            className={classNames(
              `invoice-status invoice-status-${invoiceStatusClassMap(
                rowData.status
              )}`,
              {
                underline: [
                  OInvoiceSummaryStatus.SIGNED,
                  OInvoiceSummaryStatus.SUCCESSFUL,
                  OInvoiceSummaryStatus.ERROR,
                ].includes(rowData.status),
              }
            )}
            onClick={() => {
              const isHavingLogs = [
                OInvoiceSummaryStatus.SIGNED,
                OInvoiceSummaryStatus.SUCCESSFUL,
                OInvoiceSummaryStatus.ERROR,
              ].includes(rowData.status);
              if (isHavingLogs) {
                setShowSummaryLogs(true);
                setSelectedItem(rowData);
              }
            }}
          >
            {rowData.status === OInvoiceSummaryStatus.WAIT_SIGN && "Chờ ký"}
            {rowData.status === OInvoiceSummaryStatus.WAIT_APPROVE &&
              "Chờ duyệt"}
            {rowData.status === OInvoiceSummaryStatus.SIGNED && "Đã ký"}
            {rowData.status === OInvoiceSummaryStatus.SENT && "Đang gửi"}
            {rowData.status === OInvoiceSummaryStatus.SUCCESSFUL &&
              "Thành công"}
            {rowData.status === OInvoiceSummaryStatus.ERROR && "Có lỗi"}
            {rowData.status === OInvoiceSummaryStatus.REFUSE && "Từ chối"}
          </span>
        );
      },
    },
    {
      field: OInvoiceSummaryField.CREATION_DATE,
      header: "Ngày lập",
      width: 120,
      dataType: "custom",
      filterType: "range-date",
      matchMode: "contains",
      filterField: "fromCreationDate@toCreationDate",
      customCell: (rowData) => {
        return (
          <span>
            {rowData.creationDate
              ? new Date(rowData.creationDate).toLocaleDateString("en-GB")
              : ""}
          </span>
        );
      },
    },
    {
      field: OInvoiceSummaryField.IS_FIRST_SUBMISSION,
      header: "Lần nộp",
      width: 120,
      matchMode: "contains",
      filterType: "dropdown",
      dataType: "custom",
      customCell: (rowData) => {
        return (
          <span>{rowData.isFirstSubmission ? "Lần đầu" : "Nộp bổ sung"}</span>
        );
      },
      filterOptions: [
        {
          label: "Lần đầu",
          value: true,
        },
        {
          label: "Nộp bổ sung",
          value: false,
        },
      ],
    },
    {
      field: OInvoiceSummaryField.DATA_PERIOD,
      header: "Kỳ dữ liệu",
      width: 120,
      matchMode: "contains",
      dataType: "custom",
      filterType: "dropdown",
      customCell: (rowData) => {
        return <span>{convertDataPeriod(rowData.dataPeriod)}</span>;
      },
      filterOptions: [
        {
          label: convertDataPeriod(ODataPeriod.DAY),
          value: ODataPeriod.DAY,
        },
        {
          label: convertDataPeriod(ODataPeriod.MONTH),
          value: ODataPeriod.MONTH,
        },
        {
          label: convertDataPeriod(ODataPeriod.QUARTER),
          value: ODataPeriod.QUARTER,
        },
      ],
    },
    {
      field: OInvoiceSummaryField.MONTH_OR_QUARTER,
      header: "Tháng/Quý",
      width: 120,
      matchMode: "contains",
    },
    {
      field: OInvoiceSummaryField.YEAR,
      header: "Năm",
      width: 120,
      matchMode: "contains",
    },
    {
      field: OInvoiceSummaryField.SUMMARY_DATA_NUMBER,
      header: "Số bảng tổng hợp",
      width: 150,
      matchMode: "contains",
    },
    {
      field: OInvoiceSummaryField.TOTAL_SUBMIT,
      header: "Số lần bổ sung",
      width: 130,
      matchMode: "contains",
    },
    {
      field: OInvoiceSummaryField.CURRENCY_CODE,
      header: "Đơn vị tiền tệ",
      filterOptions: currencies.map((currency: any) => {
        return {
          label: currency.code,
          value: currency.code,
        };
      }),
      filterType: "dropdown",
      width: 130,
      matchMode: "contains",
    },
    {
      field: OInvoiceSummaryField.CREATOR,
      header: "Người lập",
      matchMode: "contains",
      width: 120,
    },
    {
      field: OInvoiceSummaryField.SIGN_DATE,
      header: "Ngày giờ ký",
      filterType: "range-date",
      matchMode: "contains",
      filterField: "fromSignDate@toSignDate", // Tên trường trong API để filter theo thời gian, ví dụ: fromDate@toDate, fromDate là tên trường bắt đầu, toDate là tên trường kết thúc, @ là dấu phân cách giữa 2 trúng
      dataType: "custom",
      customCell: (rowData) => {
        return (
          <span>
            {rowData.signDate
              ? new Date(rowData.signDate).toLocaleString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })
              : ""}
          </span>
        );
      },
      width: 120,
    },
    {
      field: OInvoiceSummaryField.NOTE,
      header: "Ghi chú",
      matchMode: "contains",
      width: 120,
    },
  ];

  const {
    render: renderDataTable,
    selectedItems,
    reloadLazyData,
  } = useDataTable({
    columns: BangTongHopColumns,
    header: "Bảng tổng hợp",
    indexColumnWidth: 40,
    actionColumnWidth: 200,

    getList: async (param) => {
      return InvoiceSummaryService.getInvoiceSummaries(param).then((data) => {
        let summaryDataNumber = 0;
        data.items.forEach((item) => {
          if (item.summaryDataNumber > summaryDataNumber) {
            summaryDataNumber = item.summaryDataNumber;
          }
        });

        setHighestSummaryDataNumber(summaryDataNumber);
        return { items: data.items, totalCount: data.totalCount };
      });
    },

    initActions: (items) => {
      items.forEach((item: InvoiceSummary & { actions: any }) => {
        item.actions = [];
        item.actions.push({
          icon: "pi pi-book",
          className: "",
          title: "Xem chi tiết",
          command: () => {
            setSelectedItem(item);
            setShowDetail(true);
          },
        });
        item.actions.push({
          icon: "pi pi-eye",
          className: "",
          title: "Xem in",
          command: () => {
            LoadingService.start();
            setSelectedItem(item);
            setShowView(true);
          },
        });
        item.actions.push({
          icon: "pi pi-pencil",
          className: "p-button-warning",
          title: "Chỉnh sửa",
          command: () => {
            setEditMode(true);
            setSelectedItem(item);
            setShowUpdate(true);
            if (
              !([OInvoiceSummaryStatus.WAIT_SIGN] as number[]).includes(
                item.status
              )
            ) {
              setDisableUpdate(true);
            } else {
              setDisableUpdate(false);
            }
          },
        });
        item.actions.push({
          icon: "pi pi-plus",
          className: "p-button-success",
          title: "Bổ sung",
          disabled: !([OInvoiceSummaryStatus.SUCCESSFUL] as number[]).includes(
            item.status
          ),
          command: () => {
            setSelectedItem(item);
            setShowUpdate(true);
            setEditMode(false);
          },
        });
        item.actions.push({
          title: "Ký và gửi CQT",
          disabled: (
            [
              OInvoiceSummaryStatus.SUCCESSFUL,
              OInvoiceSummaryStatus.SIGNED,
              OInvoiceSummaryStatus.ERROR,
              OInvoiceSummaryStatus.SENT,
            ] as number[]
          ).includes(item.status),
          icon: "pi pi-user-edit",
          className: "",
          command: () => {
            signInvoiceSummary(item);
          },
        });
      });
    },
  });

  const { renderSign, sign } = useSign({ onComplete: reloadLazyData });

  const signInvoiceSummary = (item) => {
    sign({
      CommandName: "SignSummary",
      InvoiceIdArray: [item["id"]],
    });
  };

  const { renderToolbar } = useToolbar({
    dataKey: "id",
    selectedItems,
    reloadLazyData,
    create: () => {
      setShowCreate(true);
    },
    deleteItem(id) {
      return InvoiceSummaryService.deleteInvoiceSummary(id);
    },

    validateDeleteItems: (items) => {
      if (Array.isArray(items)) {
        const invalideItems = items.filter(
          (item: InvoiceSummary) =>
            (
              [
                OInvoiceSummaryStatus.SUCCESSFUL,
                OInvoiceSummaryStatus.SIGNED,
              ] as number[]
            ).includes(item.status) ||
            item.summaryDataNumber < highestSummaryDataNumber
        );
        if (invalideItems.length > 0) {
          ToastService.error(
            "Bảng tổng hợp đã ký hoặc trước bảng tổng hợp lớn nhất không thể xóa"
          );
          return false;
        }
      } else {
        if (
          (
            [
              OInvoiceSummaryStatus.SUCCESSFUL,
              OInvoiceSummaryStatus.SIGNED,
            ] as number[]
          ).includes(items.status) ||
          items.summaryDataNumber < highestSummaryDataNumber
        ) {
          ToastService.error(
            "Bảng tổng hợp đã ký hoặc trước bảng tổng hợp lớn nhất không thể xóa"
          );
          return false;
        }
      }

      return true;
    },
    otherActions: [
      {
        label: "Chức năng",
        icon: "pi pi-fw pi-th-large",
        className: "iam-toolbar iam-toolbar-help",
        items: [
          [
            {
              label: "",
              items: [
                {
                  label: "Kết xuất Excel",
                  command: () => exportExcel(selectedItems),
                },
              ],
            },
            {
              label: "",
              items: [
                {
                  label: "Tải XML",
                  command: () => downloadXml(selectedItems),
                },
              ],
            },
          ],
        ],
      },
    ],
  });

  useEffect(() => {
    CurrencyService.getList({ skipCount: 0, maxResultCount: 50 }).then(
      (data) => {
        setCurrencies(data.items);
      }
    );
  }, []);
  return (
    <>

      {renderToolbar()}
      {renderDataTable()}
      {renderSign()}
      <DetailDialog
        open={showDetail}
        onHide={setShowDetail}
        id={selectedItem?.id}
        summaryData={selectedItems}
      />
      <CreateInvoiceSummaryDialog
        open={showCreate}
        onHide={setShowCreate}
        currencies={currencies}
        refreshLazyData={reloadLazyData}
      />
      <UpdateInvoiceSummaryDialog
        open={showUpdate}
        isEditMode={editMode}
        onHide={setShowUpdate}
        invoiceSummaryData={selectedItem}
        currencies={currencies}
        refreshLazyData={reloadLazyData}
        disableSubmit={disableUpdate}
      />
      <ViewSummaryDialog
        onHide={setShowView}
        open={showView}
        data={selectedItem}
      />
      <SummaryLog
        open={showSummaryLogs}
        onHide={() => setShowSummaryLogs(false)}
        invoiceSummaryData={selectedItem}
      />
    </>
  );
}
